import { useState, useEffect, useRef, useMemo } from "react";

/* ══════════════════════════════════════════════════════════
   BARCODE LABEL PRINTER  v2
   ──────────────────────────────────────────────────────────
   Props:
     item     – inventory item object
     product  – full product object
                fields used: productCode, purityStandard, vendorID
     vendors  – full vendors array (to resolve vendorName)
     onClose  – fn

   Parent usage:
     <BarcodeLabelPrinter
       item={printItem}
       product={products.find(p => p.productID === printItem.productID)}
       vendors={vendors}
       onClose={() => setPrintItem(null)}
     />

   KEY FIX: Barcode encodes ONLY inventoryItemID (short number),
            width:2 bars — any handheld scanner will read it.
══════════════════════════════════════════════════════════ */

const fmt   = (v) => (parseFloat(v) || 0).toFixed(3);
const mkAbbr = (s = "") =>
  s.split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 4) || "RJ";

/* tiny DOM element helper */
function el(tag, css = "") {
  const d = document.createElement(tag);
  if (css) d.style.cssText = css;
  return d;
}

/* ══════════════════════════════════════════════════════════
   BARCODE SVG
   width: 2   → thick bars, scannable by every gun/phone
   displayValue: false → we render the number ourselves
══════════════════════════════════════════════════════════ */
function makeSVGBarcode(value, widthAttr, height, bg = "#ffffff") {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  try {
    window.JsBarcode(svg, String(value), {
      format: "CODE128", width: 2, height,
      displayValue: false, margin: 2,
      background: bg, lineColor: "#000000",
    });
  } catch (_) {}
  if (widthAttr) svg.setAttribute("width",  String(widthAttr));
  if (height)    svg.setAttribute("height", String(height));
  return svg;
}

/* ══════════════════════════════════════════════════════════
   DOM BUILDERS — used for the live preview panel
══════════════════════════════════════════════════════════ */

/* ── HORIZONTAL TAG (86 × 38 mm) ── */
function buildHorizTag(item, product, vendorName, shopAbbr) {
  const code   = product?.productCode || `#${item.productID}`;
  const purity = product?.purityStandard || "—";
  const gw = fmt(item.grossWeight);
  const lw = fmt(item.lessWeight);
  const nw = fmt(item.netWeight);

  const wrap = el("div", `
    width:330px;height:120px;background:#d4f4f9;
    border:1.5px solid #7ecfdb;border-radius:8px;
    display:flex;flex-direction:row;
    font-family:'Courier New',monospace;color:#0d0d0d;
    position:relative;overflow:hidden;flex-shrink:0;
    box-shadow:2px 3px 10px rgba(0,0,0,.12);
  `);

  /* punch hole */
  wrap.appendChild(el("div", `
    position:absolute;top:50%;left:-6px;transform:translateY(-50%);
    width:13px;height:13px;border-radius:50%;
    background:#fff;border:1.5px solid #7ecfdb;z-index:2;
  `));

  /* LEFT */
  const left = el("div", `
    flex:0 0 44%;padding:10px 5px 10px 15px;
    display:flex;flex-direction:column;justify-content:center;gap:1px;
  `);

  const codeEl = el("div", `font-size:18px;font-weight:900;letter-spacing:1px;color:#0a0a0a;margin-bottom:5px;line-height:1;`);
  codeEl.textContent = code;
  left.appendChild(codeEl);

  [["G", gw, false], ["L", lw, false], ["N", nw, true]].forEach(([lbl, val, bold]) => {
    const row = el("div", `font-size:9.5px;line-height:1.75;color:#1a1a1a;`);
    row.innerHTML = `<span style="display:inline-block;width:9px;font-weight:700;">${lbl}</span>: ${
      bold ? `<strong>${val} g</strong>` : `${val} g`
    }`;
    left.appendChild(row);
  });

  if (vendorName) {
    const vn = el("div", `
      font-size:7.5px;color:#3a8a96;margin-top:5px;
      font-family:sans-serif;font-weight:700;
      text-transform:uppercase;letter-spacing:.7px;
    `);
    vn.textContent = vendorName;
    left.appendChild(vn);
  }
  wrap.appendChild(left);

  /* divider */
  wrap.appendChild(el("div", `width:1px;background:#7ecfdb;margin:8px 0;flex-shrink:0;`));

  /* RIGHT */
  const right = el("div", `
    flex:1;padding:7px 10px;
    display:flex;flex-direction:column;
    align-items:center;justify-content:space-between;
  `);

  const shopEl = el("div", `
    font-size:14px;font-weight:900;letter-spacing:4px;
    color:#0a0a0a;font-family:sans-serif;text-align:center;
  `);
  shopEl.textContent = shopAbbr;
  right.appendChild(shopEl);

  /* barcode — encodes ONLY inventoryItemID */
  const bcWrap = el("div", `width:100%;display:flex;flex-direction:column;align-items:center;gap:2px;`);
  bcWrap.appendChild(makeSVGBarcode(item.inventoryItemID, "100%", 38, "#d4f4f9"));
  const bcNum = el("div", `font-size:7px;color:#2a6a72;text-align:center;letter-spacing:.5px;`);
  bcNum.textContent = String(item.inventoryItemID);
  bcWrap.appendChild(bcNum);
  right.appendChild(bcWrap);

  const pur = el("div", `font-size:9.5px;font-weight:800;font-family:sans-serif;color:#0a0a0a;`);
  pur.textContent = purity;
  right.appendChild(pur);

  wrap.appendChild(right);
  return wrap;
}

/* ── SQUARE STICKER (50 × 50 mm) ── */
function buildSquareSticker(item, product, vendorName, shopAbbr) {
  const code   = product?.productCode || `#${item.productID}`;
  const purity = product?.purityStandard || "—";
  const gw = fmt(item.grossWeight);
  const lw = fmt(item.lessWeight);
  const nw = fmt(item.netWeight);

  const wrap = el("div", `
    width:200px;height:200px;background:#d4f4f9;
    border:1.5px solid #7ecfdb;border-radius:10px;padding:10px;
    display:flex;flex-direction:column;align-items:center;justify-content:space-between;
    font-family:'Courier New',monospace;color:#0d0d0d;
    flex-shrink:0;box-shadow:2px 3px 10px rgba(0,0,0,.12);
  `);

  /* top: shop abbr | product code */
  const top = el("div", `width:100%;display:flex;justify-content:space-between;align-items:center;`);
  const sa = el("div", `font-size:13px;font-weight:900;letter-spacing:3px;font-family:sans-serif;color:#0a0a0a;`);
  sa.textContent = shopAbbr;
  top.appendChild(sa);
  const pc = el("div", `font-size:12px;font-weight:900;letter-spacing:.5px;`);
  pc.textContent = code;
  top.appendChild(pc);
  wrap.appendChild(top);

  wrap.appendChild(el("div", `width:100%;height:.5px;background:#7ecfdb;`));

  /* weights */
  const wts = el("div", `width:100%;display:flex;flex-direction:column;gap:1px;font-size:9.5px;color:#1a1a1a;`);
  [["G", gw, false], ["L", lw, false], ["N", nw, true]].forEach(([lbl, val, bold]) => {
    const row = el("div", `line-height:1.75;`);
    row.innerHTML = `<span style="display:inline-block;width:9px;font-weight:700;">${lbl}</span>: ${
      bold ? `<strong>${val} g</strong>` : `${val} g`
    }`;
    wts.appendChild(row);
  });
  if (vendorName) {
    const vn = el("div", `font-size:7.5px;color:#3a8a96;margin-top:3px;font-weight:700;font-family:sans-serif;text-transform:uppercase;`);
    vn.textContent = vendorName;
    wts.appendChild(vn);
  }
  wrap.appendChild(wts);

  wrap.appendChild(el("div", `width:100%;height:.5px;background:#7ecfdb;`));

  /* barcode — encodes ONLY inventoryItemID */
  const bcWrap = el("div", `width:100%;display:flex;flex-direction:column;align-items:center;gap:2px;`);
  bcWrap.appendChild(makeSVGBarcode(item.inventoryItemID, "100%", 40, "#d4f4f9"));
  const bcNum = el("div", `font-size:7px;color:#2a6a72;text-align:center;letter-spacing:1px;`);
  bcNum.textContent = String(item.inventoryItemID);
  bcWrap.appendChild(bcNum);
  wrap.appendChild(bcWrap);

  const pur = el("div", `font-size:9px;font-weight:800;font-family:sans-serif;color:#0a0a0a;`);
  pur.textContent = purity;
  wrap.appendChild(pur);

  return wrap;
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function BarcodeLabelPrinter({
  item,
  items = [],
  product,
  products = [],
  vendors = [],
  vendorName: defaultVendorName = "",
  onClose
}) {
  const normItems = useMemo(
    () => (items && items.length > 0 ? items : item ? [item] : []),
    [items, item]
  );
  const [activeIdx, setActiveIdx] = useState(0);
  const [tab,        setTab]        = useState("tag");   // "tag" | "sticker"
  const [copies,     setCopies]     = useState(1);
  const [shopName,   setShopName]   = useState("REVA JEWELLERS");
  const [shopAbbr,   setShopAbbr]   = useState("RJ");
  const [generating, setGenerating] = useState(false);
  const previewRef = useRef(null);

  const isBatch = normItems.length > 1;
  const currentItem = useMemo(() => normItems[activeIdx] || normItems[0] || {}, [normItems, activeIdx]);
  const currentProduct = useMemo(
    () => product || products.find((p) => p.productID === currentItem?.productID),
    [product, products, currentItem?.productID]
  );
  const currentVendorName = useMemo(
    () =>
      defaultVendorName ||
      vendors.find((v) => v.vendorID === currentProduct?.vendorID)?.vendorName ||
      "",
    [defaultVendorName, vendors, currentProduct?.vendorID]
  );

  /* live preview */
  useEffect(() => {
    if (!previewRef.current) return;
    previewRef.current.innerHTML = "";
    const show = Math.min(copies, 2);
    for (let i = 0; i < show; i++) {
      const node =
        tab === "tag"
          ? buildHorizTag(currentItem, currentProduct, currentVendorName, shopAbbr)
          : buildSquareSticker(currentItem, currentProduct, currentVendorName, shopAbbr);
      previewRef.current.appendChild(node);
    }
    if (copies > 2) {
      const note = el("p", "width:100%;text-align:center;font-size:11px;color:#999;margin-top:10px;font-family:sans-serif;");
      note.textContent = `+ ${copies - 2} more ${copies - 2 === 1 ? "copy" : "copies"}`;
      previewRef.current.appendChild(note);
    }
  }, [currentItem, currentProduct, currentVendorName, copies, tab, shopAbbr]);

  /* barcode → data URL for PDF */
  function getBarcodeDataURL(value, height, bg = "#d4f4f9") {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    try {
      window.JsBarcode(svg, String(value), {
        format: "CODE128", width: 2, height,
        displayValue: false, margin: 2,
        background: bg, lineColor: "#000000",
      });
    } catch (_) {}
    const s = new XMLSerializer().serializeToString(svg);
    return "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(s)));
  }

  async function svgToPNG(dataUrl, w, h, bg = "#d4f4f9") {
    return new Promise((res) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        res(c.toDataURL("image/png"));
      };
      img.src = dataUrl;
    });
  }

  /* ── Download PDF ── */
  const downloadPDF = async () => {
    setGenerating(true);
    try {
      const { jsPDF } = window.jspdf;
      const targetItems = isBatch ? normItems : [currentItem];

      if (tab === "tag") {
        /* Landscape 86 × 38 mm */
        const W = 86, H = 38;
        const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: [H, W] });
        let isFirstPage = true;

        for (let itemIdx = 0; itemIdx < targetItems.length; itemIdx++) {
          const it = targetItems[itemIdx];
          const itProd = product || products.find((p) => p.productID === it.productID);
          const itVendor = defaultVendorName || vendors.find((v) => v.vendorID === itProd?.vendorID)?.vendorName || "";
          const code   = itProd?.productCode || `#${it.productID}`;
          const purity = itProd?.purityStandard || "—";
          const gw = fmt(it.grossWeight);
          const lw = fmt(it.lessWeight);
          const nw = fmt(it.netWeight);
          const itId = it.barcodeID || it.inventoryItemID || 0;
          const bcPNG = await svgToPNG(getBarcodeDataURL(itId, 80, "#d4f4f9"), 580, 80, "#d4f4f9");

          for (let i = 0; i < copies; i++) {
            if (!isFirstPage) pdf.addPage([H, W], "landscape");
            isFirstPage = false;

            pdf.setFillColor(212, 244, 249); pdf.rect(0, 0, W, H, "F");
            pdf.setDrawColor(126, 207, 219); pdf.setLineWidth(0.35);
            pdf.roundedRect(.5, .5, W - 1, H - 1, 2.5, 2.5, "S");

            pdf.setFillColor(255, 255, 255); pdf.setDrawColor(126, 207, 219);
            pdf.circle(2.5, H / 2, 2, "FD");

            /* LEFT */
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(10, 10, 10);
            pdf.text(code, 6, 9);
            pdf.setFont("courier", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(25, 25, 25);
            pdf.text(`G: ${gw} g`, 6, 16);
            pdf.text(`L: ${lw} g`, 6, 21.5);
            pdf.setFont("courier", "bold");
            pdf.text(`N: ${nw} g`, 6, 27);
            if (itVendor) {
              pdf.setFont("helvetica", "bold"); pdf.setFontSize(5.5); pdf.setTextColor(55, 138, 150);
              pdf.text(itVendor.toUpperCase(), 6, H - 3);
            }

            pdf.setDrawColor(126, 207, 219); pdf.setLineWidth(.3);
            pdf.line(40, 3, 40, H - 3);

            /* RIGHT */
            const rx = 43, rcx = rx + (W - rx - 2) / 2;
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(11);
            pdf.setTextColor(10, 10, 10); pdf.setCharSpace(2);
            pdf.text(shopAbbr, rcx, 7, { align: "center" }); pdf.setCharSpace(0);

            pdf.addImage(bcPNG, "PNG", rx, 10, W - rx - 2, 13);

            pdf.setFont("courier", "normal"); pdf.setFontSize(5.5); pdf.setTextColor(42, 106, 114);
            pdf.text(String(itId), rcx, 25.5, { align: "center" });

            pdf.setFont("helvetica", "bold"); pdf.setFontSize(7.5); pdf.setTextColor(10, 10, 10);
            pdf.text(purity, rcx, H - 3, { align: "center" });
          }
        }
        pdf.save(isBatch ? `reva-tags-batch-${targetItems.length}items.pdf` : `tag-item${currentItem.inventoryItemID}-x${copies}.pdf`);

      } else {
        /* Portrait 50 × 50 mm */
        const W = 50, H = 50;
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [W, H] });
        let isFirstPage = true;

        for (let itemIdx = 0; itemIdx < targetItems.length; itemIdx++) {
          const it = targetItems[itemIdx];
          const itProd = product || products.find((p) => p.productID === it.productID);
          const itVendor = defaultVendorName || vendors.find((v) => v.vendorID === itProd?.vendorID)?.vendorName || "";
          const code   = itProd?.productCode || `#${it.productID}`;
          const purity = itProd?.purityStandard || "—";
          const gw = fmt(it.grossWeight);
          const lw = fmt(it.lessWeight);
          const nw = fmt(it.netWeight);
          const itId = it.barcodeID || it.inventoryItemID || 0;
          const bcPNG = await svgToPNG(getBarcodeDataURL(itId, 70, "#d4f4f9"), 480, 70, "#d4f4f9");

          for (let i = 0; i < copies; i++) {
            if (!isFirstPage) pdf.addPage([W, H]);
            isFirstPage = false;

            pdf.setFillColor(212, 244, 249); pdf.rect(0, 0, W, H, "F");
            pdf.setDrawColor(126, 207, 219); pdf.setLineWidth(.35);
            pdf.roundedRect(.5, .5, W - 1, H - 1, 3, 3, "S");

            /* top row */
            pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(10, 10, 10);
            pdf.setCharSpace(2); pdf.text(shopAbbr, 4, 7); pdf.setCharSpace(0);
            pdf.setFont("courier", "bold"); pdf.setFontSize(10);
            pdf.text(code, W - 4, 7, { align: "right" });

            pdf.setDrawColor(126, 207, 219); pdf.line(3, 9.5, W - 3, 9.5);

            /* weights */
            pdf.setFont("courier", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(25, 25, 25);
            pdf.text(`G: ${gw} g`, 4, 15.5);
            pdf.text(`L: ${lw} g`, 4, 21);
            pdf.setFont("courier", "bold");
            pdf.text(`N: ${nw} g`, 4, 26.5);

            if (itVendor) {
              pdf.setFont("helvetica", "bold"); pdf.setFontSize(5.5); pdf.setTextColor(55, 138, 150);
              pdf.text(itVendor.toUpperCase(), 4, 31);
            }

            const bcY = itVendor ? 33 : 29;
            pdf.setDrawColor(126, 207, 219); pdf.line(3, bcY - 1, W - 3, bcY - 1);
            pdf.addImage(bcPNG, "PNG", 3, bcY, W - 6, 11);

            pdf.setFont("courier", "normal"); pdf.setFontSize(5.5); pdf.setTextColor(42, 106, 114);
            pdf.text(String(itId), W / 2, bcY + 13, { align: "center" });

            pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.setTextColor(10, 10, 10);
            pdf.text(purity, W / 2, H - 2.5, { align: "center" });
          }
        }
        pdf.save(isBatch ? `reva-stickers-batch-${targetItems.length}items.pdf` : `sticker-item${currentItem.inventoryItemID}-x${copies}.pdf`);
      }
    } finally {
      setGenerating(false);
    }
  };

  /* ── Print ── */
  const printLabels = () => {
    const targetItems = isBatch ? normItems : [currentItem];
    let html = "";

    targetItems.forEach((it) => {
      const itProd = product || products.find((p) => p.productID === it.productID);
      const itVendor = defaultVendorName || vendors.find((v) => v.vendorID === itProd?.vendorID)?.vendorName || "";
      const code   = itProd?.productCode || `#${it.productID}`;
      const purity = itProd?.purityStandard || "—";
      const gw = fmt(it.grossWeight);
      const lw = fmt(it.lessWeight);
      const nw = fmt(it.netWeight);
      const itId = it.barcodeID || it.inventoryItemID || 0;

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      try {
        window.JsBarcode(svg, String(itId), {
          format: "CODE128", width: 2, height: 50,
          displayValue: false, margin: 2,
          background: "#d4f4f9", lineColor: "#000000",
        });
      } catch (_) {}
      const svgB64 = "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svg))));

      for (let i = 0; i < copies; i++) {
        if (tab === "tag") {
          html += `
            <div class="tag-h">
              <div class="hole"></div>
              <div class="left">
                <div class="prod-code">${code}</div>
                <div class="wrow">G: ${gw} g</div>
                <div class="wrow">L: ${lw} g</div>
                <div class="wrow wnet">N: <strong>${nw} g</strong></div>
                ${itVendor ? `<div class="vendor">${itVendor}</div>` : ""}
              </div>
              <div class="dvdr-v"></div>
              <div class="right">
                <div class="shop-abbr">${shopAbbr}</div>
                <div class="bc-blk">
                  <img class="bc-img" src="${svgB64}" />
                  <div class="bc-id">${itId}</div>
                </div>
                <div class="purity">${purity}</div>
              </div>
            </div>`;
        } else {
          html += `
            <div class="sticker-sq">
              <div class="s-top">
                <span class="s-abbr">${shopAbbr}</span>
                <span class="s-code">${code}</span>
              </div>
              <div class="dvdr-h"></div>
              <div class="s-wts">
                <div class="wrow">G: ${gw} g</div>
                <div class="wrow">L: ${lw} g</div>
                <div class="wrow wnet">N: <strong>${nw} g</strong></div>
                ${itVendor ? `<div class="vendor">${itVendor}</div>` : ""}
              </div>
              <div class="dvdr-h"></div>
              <div class="bc-blk">
                <img class="bc-img-sq" src="${svgB64}" />
                <div class="bc-id">${itId}</div>
              </div>
              <div class="purity">${purity}</div>
            </div>`;
        }
      }
    });

    const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>${isBatch ? `Labels — Consignment (${targetItems.length} items)` : `Label — Item #${currentItem?.inventoryItemID || ""}`}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:#e0e0e0;display:flex;flex-wrap:wrap;gap:10px;padding:12px;align-items:flex-start;}

  /* HORIZONTAL TAG */
  .tag-h{
    width:86mm;height:38mm;background:#d4f4f9;
    border:1.5px solid #7ecfdb;border-radius:5px;
    display:flex;flex-direction:row;
    font-family:'Courier New',monospace;color:#0d0d0d;
    position:relative;overflow:hidden;page-break-inside:avoid;
  }
  .hole{position:absolute;top:50%;left:-5px;transform:translateY(-50%);
    width:10px;height:10px;border-radius:50%;background:#fff;border:1.5px solid #7ecfdb;}
  .left{flex:0 0 44%;padding:8px 5px 8px 12px;display:flex;flex-direction:column;justify-content:center;gap:1px;}
  .prod-code{font-size:15pt;font-weight:900;letter-spacing:1px;margin-bottom:3px;line-height:1;}
  .wrow{font-size:7.5pt;line-height:1.75;}
  .wnet{font-size:8pt;}
  .vendor{font-size:6pt;color:#3a8a96;font-family:sans-serif;font-weight:700;text-transform:uppercase;letter-spacing:.7px;margin-top:3px;}
  .dvdr-v{width:1px;background:#7ecfdb;margin:6px 0;flex-shrink:0;}
  .right{flex:1;padding:6px 8px;display:flex;flex-direction:column;align-items:center;justify-content:space-between;}
  .shop-abbr{font-size:11pt;font-weight:900;letter-spacing:4px;font-family:sans-serif;}
  .bc-blk{display:flex;flex-direction:column;align-items:center;width:100%;}
  .bc-img{width:100%;height:12mm;object-fit:fill;}
  .bc-id{font-size:6pt;color:#2a6a72;margin-top:1px;}
  .purity{font-size:7.5pt;font-weight:800;font-family:sans-serif;}

  /* SQUARE STICKER */
  .sticker-sq{
    width:50mm;height:50mm;background:#d4f4f9;
    border:1.5px solid #7ecfdb;border-radius:6px;padding:7px;
    display:flex;flex-direction:column;align-items:center;justify-content:space-between;
    font-family:'Courier New',monospace;color:#0d0d0d;
    page-break-inside:avoid;
  }
  .s-top{width:100%;display:flex;justify-content:space-between;align-items:center;}
  .s-abbr{font-size:9pt;font-weight:900;letter-spacing:3px;font-family:sans-serif;}
  .s-code{font-size:9pt;font-weight:900;}
  .dvdr-h{width:100%;height:.5px;background:#7ecfdb;}
  .s-wts{width:100%;display:flex;flex-direction:column;}
  .bc-img-sq{width:100%;height:10mm;object-fit:fill;}

  @media print{
    body{background:#fff;padding:4mm;gap:5mm;}
    @page{margin:4mm;size:auto;}
  }
</style>
</head><body>${html}
<script>
  window.onload = function() {
    setTimeout(function() {
      window.print();
    }, 250);
  };
</script>
</body></html>`;

    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank");
    if (win) {
      win.focus();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  };

  const handleShopName = (val) => {
    setShopName(val);
    setShopAbbr(mkAbbr(val));
  };

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="blp-back" onClick={onClose}>
      <div className="blp-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="blp-header">
          <div className="blp-hicon"><TagIcon /></div>
          <div>
            <h3 className="blp-title">
              {isBatch ? `Print Consignment Tags (${normItems.length} Items)` : `Print Label — Item #${currentItem.inventoryItemID || "—"}`}
            </h3>
            <p className="blp-sub">
              {isBatch
                ? `Inward consignment batch · Previewing item ${activeIdx + 1} of ${normItems.length}`
                : `${currentProduct?.productCode ? `${currentProduct.productCode} · ` : ""}${currentProduct?.productName || `Product #${currentItem.productID}`}`
              }
            </p>
          </div>
          <button className="blp-xbtn" onClick={onClose}><XIcon /></button>
        </div>

        <div className="blp-body">

          {/* Batch Item Selector Bar if batch */}
          {isBatch && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              background: "linear-gradient(135deg, rgba(42,138,150,.1) 0%, rgba(212,244,249,.35) 100%)",
              borderRadius: 10,
              border: "1px solid rgba(126,207,219,.4)"
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#2a8a96", textTransform: "uppercase" }}>
                  Selected Consignment Item
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1a1c" }}>
                  {currentProduct?.productName || `Product #${currentItem.productID}`} · <span style={{ color: "#2a8a96" }}>#{currentItem.inventoryItemID || "New"}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  type="button"
                  style={{
                    padding: "5px 10px",
                    borderRadius: 6,
                    border: "1px solid rgba(126,207,219,.5)",
                    background: "#fff",
                    color: "#2a8a96",
                    fontWeight: 700,
                    cursor: activeIdx === 0 ? "not-allowed" : "pointer",
                    opacity: activeIdx === 0 ? 0.4 : 1
                  }}
                  disabled={activeIdx === 0}
                  onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
                >
                  ◀ Prev
                </button>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#0d1a1c", minWidth: 46, textAlign: "center" }}>
                  {activeIdx + 1} / {normItems.length}
                </span>
                <button
                  type="button"
                  style={{
                    padding: "5px 10px",
                    borderRadius: 6,
                    border: "1px solid rgba(126,207,219,.5)",
                    background: "#fff",
                    color: "#2a8a96",
                    fontWeight: 700,
                    cursor: activeIdx === normItems.length - 1 ? "not-allowed" : "pointer",
                    opacity: activeIdx === normItems.length - 1 ? 0.4 : 1
                  }}
                  disabled={activeIdx === normItems.length - 1}
                  onClick={() => setActiveIdx(i => Math.min(normItems.length - 1, i + 1))}
                >
                  Next ▶
                </button>
              </div>
            </div>
          )}

          {/* Shop name + abbr */}
          <div className="blp-row2">
            <div className="blp-field">
              <label className="blp-lbl">Shop Name</label>
              <input className="blp-inp" type="text" maxLength={36} value={shopName}
                onChange={(e) => handleShopName(e.target.value)} />
            </div>
            <div className="blp-field blp-field-narrow">
              <label className="blp-lbl">Abbr. on Tag</label>
              <input className="blp-inp blp-inp-abbr" type="text" maxLength={5} value={shopAbbr}
                onChange={(e) => setShopAbbr(e.target.value.toUpperCase())} />
            </div>
          </div>

          {/* ── LABEL TYPE — two clear cards ── */}
          <div className="blp-field">
            <label className="blp-lbl">Label Type</label>
            <div className="blp-type-grid">

              <button
                className={`blp-type-card ${tab === "tag" ? "blp-type-card--on" : ""}`}
                onClick={() => setTab("tag")}
              >
                <div className="blp-type-top">
                  <span className="blp-type-ico"><HorizTagIcon /></span>
                  {tab === "tag" && <span className="blp-check-dot"><CheckIcon /></span>}
                </div>
                <span className="blp-type-name">Horizontal Tag</span>
                <span className="blp-type-dim">86 × 38 mm · Hang tag with hole</span>
              </button>

              <button
                className={`blp-type-card ${tab === "sticker" ? "blp-type-card--on" : ""}`}
                onClick={() => setTab("sticker")}
              >
                <div className="blp-type-top">
                  <span className="blp-type-ico"><StickerIcon /></span>
                  {tab === "sticker" && <span className="blp-check-dot"><CheckIcon /></span>}
                </div>
                <span className="blp-type-name">Square Sticker</span>
                <span className="blp-type-dim">50 × 50 mm · Adhesive label</span>
              </button>

            </div>
          </div>

          {/* Copies */}
          <div className="blp-copies">
            <label className="blp-lbl" style={{ marginBottom: 0, whiteSpace: "nowrap" }}>
              {isBatch ? "Copies Per Item" : "Copies"}
            </label>
            <div className="blp-stepper">
              <button className="blp-step" onClick={() => setCopies((c) => Math.max(1, c - 1))}>−</button>
              <input className="blp-inp blp-step-inp" type="number" min={1} max={200} value={copies}
                onChange={(e) => setCopies(Math.max(1, Math.min(200, Number(e.target.value))))} />
              <button className="blp-step" onClick={() => setCopies((c) => Math.min(200, c + 1))}>+</button>
            </div>
          </div>

          {/* Preview */}
          <div className="blp-field">
            <label className="blp-lbl">
              Preview {isBatch ? `(Item ${activeIdx + 1} of ${normItems.length})` : ""}
            </label>
            <div className="blp-preview">
              <div ref={previewRef} className="blp-preview-inner" />
            </div>
            <p className="blp-bc-note">
              {isBatch
                ? `✓ Batch print: ${normItems.length} items will be printed with Code128 barcodes.`
                : `✓ Barcode encodes Item ID #${currentItem.inventoryItemID || "—"} only — scannable by any barcode gun`
              }
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="blp-footer">
          <button className="blp-btn blp-ghost" onClick={onClose}>Cancel</button>
          <button className="blp-btn blp-ghost" onClick={printLabels}>
            <PrintIcon /> {isBatch ? `Print All (${normItems.length} Tags)` : "Print"}
          </button>
          <button className="blp-btn blp-primary" onClick={downloadPDF} disabled={generating}>
            {generating ? <span className="blp-spin" /> : <DownloadIcon />}
            {generating ? "Generating…" : (isBatch ? `Download All (${normItems.length} Tags PDF)` : "Download PDF")}
          </button>
        </div>

      </div>

      <style>{`
        .blp-back{
          position:fixed;inset:0;z-index:9999;
          background:rgba(8,22,27,.58);backdrop-filter:blur(5px);
          display:flex;align-items:center;justify-content:center;padding:16px;
        }
        .blp-modal{
          background:#fff;border-radius:16px;
          max-width:560px;width:100%;max-height:93vh;
          display:flex;flex-direction:column;
          box-shadow:0 28px 70px rgba(8,22,27,.24),0 0 0 1px rgba(126,207,219,.18);
          animation:blpUp .22s cubic-bezier(.34,1.56,.64,1);overflow:hidden;
        }
        @keyframes blpUp{from{opacity:0;transform:translateY(18px) scale(.97)}to{opacity:1;transform:none}}

        .blp-header{display:flex;align-items:center;gap:12px;padding:16px 20px;border-bottom:1px solid rgba(126,207,219,.18);flex-shrink:0;}
        .blp-hicon{width:38px;height:38px;border-radius:10px;background:#d4f4f9;display:flex;align-items:center;justify-content:center;color:#2a8a96;flex-shrink:0;}
        .blp-title{font-size:15px;font-weight:700;color:#0d1a1c;margin:0;}
        .blp-sub{font-size:12px;color:#5a9ea8;margin:2px 0 0;}
        .blp-xbtn{margin-left:auto;background:none;border:none;cursor:pointer;padding:7px;color:#8aacb4;border-radius:8px;display:flex;align-items:center;transition:background .13s;}
        .blp-xbtn:hover{background:#eef9fa;color:#2a8a96;}
        .blp-xbtn svg{width:16px;height:16px;}

        .blp-body{overflow-y:auto;flex:1;padding:18px 20px;display:flex;flex-direction:column;gap:16px;}
        .blp-field{display:flex;flex-direction:column;gap:6px;}
        .blp-row2{display:flex;gap:12px;}
        .blp-row2 .blp-field{flex:1;}
        .blp-field-narrow{flex:0 0 105px !important;}
        .blp-lbl{font-size:11px;font-weight:700;color:#4a6a72;text-transform:uppercase;letter-spacing:.6px;}
        .blp-inp{
          border:1.5px solid rgba(126,207,219,.4);border-radius:9px;
          padding:8px 12px;font-size:13.5px;color:#0d1a1c;
          background:#f7fcfd;outline:none;font-family:inherit;
          transition:border-color .14s,box-shadow .14s;
        }
        .blp-inp:focus{border-color:#7ecfdb;box-shadow:0 0 0 3px rgba(126,207,219,.2);}
        .blp-inp-abbr{font-weight:800;letter-spacing:3px;text-align:center;}

        /* LABEL TYPE CARDS */
        .blp-type-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
        .blp-type-card{
          position:relative;
          display:flex;flex-direction:column;align-items:flex-start;
          gap:4px;padding:14px;border-radius:12px;cursor:pointer;text-align:left;
          background:#f7fcfd;
          border:2px solid rgba(126,207,219,.3);
          transition:all .16s;
        }
        .blp-type-card:hover{background:#eef9fb;border-color:rgba(126,207,219,.65);}
        .blp-type-card--on{
          border-color:#2a8a96 !important;
          background:rgba(212,244,249,.55) !important;
          box-shadow:0 0 0 3px rgba(126,207,219,.2);
        }
        .blp-type-top{width:100%;display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:2px;}
        .blp-type-ico{color:#2a8a96;display:flex;}
        .blp-type-ico svg{width:22px;height:22px;}
        .blp-check-dot{
          width:18px;height:18px;border-radius:50%;
          background:#2a8a96;color:#fff;
          display:flex;align-items:center;justify-content:center;
        }
        .blp-check-dot svg{width:10px;height:10px;}
        .blp-type-name{font-size:13px;font-weight:700;color:#0d1a1c;}
        .blp-type-dim{font-size:11px;color:#7ecfdb;}

        /* COPIES */
        .blp-copies{display:flex;align-items:center;gap:12px;}
        .blp-stepper{display:flex;align-items:center;gap:4px;}
        .blp-step{
          width:32px;height:32px;border-radius:8px;
          border:1.5px solid rgba(126,207,219,.4);
          background:#f7fcfd;color:#2a8a96;font-size:18px;font-weight:700;
          cursor:pointer;display:flex;align-items:center;justify-content:center;
          transition:background .12s;
        }
        .blp-step:hover{background:#d4f4f9;}
        .blp-step-inp{width:64px;text-align:center;font-weight:700;font-size:14px;}

        /* PREVIEW */
        .blp-preview{
          background:#e2f8fa;border:1.5px solid rgba(126,207,219,.3);
          border-radius:12px;padding:20px;min-height:148px;
          display:flex;align-items:center;justify-content:center;overflow-x:auto;
        }
        .blp-preview-inner{display:flex;flex-direction:column;gap:14px;align-items:center;}
        .blp-bc-note{
          font-size:11px;color:#2a8a96;text-align:center;
          background:#eefbfc;border-radius:8px;padding:7px 12px;
          border:1px solid rgba(126,207,219,.3);
        }

        /* FOOTER */
        .blp-footer{display:flex;gap:8px;justify-content:flex-end;padding:14px 20px;border-top:1px solid rgba(126,207,219,.15);flex-shrink:0;}
        .blp-btn{display:flex;align-items:center;gap:6px;padding:9px 18px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;border:1.5px solid transparent;transition:all .14s;}
        .blp-btn svg{width:14px;height:14px;}
        .blp-ghost{background:transparent;border-color:rgba(126,207,219,.45);color:#2a8a96;}
        .blp-ghost:hover{background:#eef9fb;}
        .blp-primary{background:#2a8a96;color:#fff;border-color:#2a8a96;}
        .blp-primary:hover:not(:disabled){background:#1f6e7a;}
        .blp-primary:disabled{opacity:.6;cursor:not-allowed;}
        .blp-spin{width:13px;height:13px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;display:inline-block;animation:spin .6s linear infinite;}
        @keyframes spin{to{transform:rotate(360deg)}}

        @media(max-width:560px){
          .blp-modal{border-radius:14px 14px 0 0;max-height:96vh;}
          .blp-back{align-items:flex-end;}
          .blp-type-grid{grid-template-columns:1fr;}
        }
      `}</style>
    </div>
  );
}

/* ── Icons ── */
function XIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function PrintIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
function DownloadIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>; }
function CheckIcon()    { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function TagIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="20" height="20"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function HorizTagIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="22" height="22"><rect x="2" y="7" width="20" height="10" rx="2"/><line x1="6" y1="12" x2="6.01" y2="12" strokeWidth="2.5"/><line x1="10" y1="10" x2="20" y2="10"/><line x1="10" y1="12" x2="20" y2="12"/><line x1="10" y1="14" x2="16" y2="14"/></svg>; }
function StickerIcon()  { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="22" height="22"><rect x="3" y="3" width="18" height="18" rx="3"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/><line x1="3" y1="15" x2="9" y2="15"/></svg>; }

/* ══════════════════════════════════════════════════════════
   HOW TO USE IN InventoryItemPage.jsx
   ──────────────────────────────────────────────────────────
   1. Import at top:
        import BarcodeLabelPrinter from "./BarcodeLabelPrinter";
        import { vendorApi } from "../api/api";

   2. Add vendors state + fetch:
        const [vendors, setVendors] = useState([]);
        // inside fetchAll():
        const vendorsData = await vendorApi.getAll();
        setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData?.data ?? []);

   3. Replace old {printItem && <PrintLabelModal …/>} with:
        {printItem && (
          <BarcodeLabelPrinter
            item={printItem}
            product={products.find(p => p.productID === printItem.productID)}
            vendors={vendors}
            onClose={() => setPrintItem(null)}
          />
        )}

   Product fields read from your api.js:
     product.productCode     – e.g. "DVI78"
     product.purityStandard  – e.g. "925 Silver", "22K"
     product.vendorID        – matched to vendors[] → vendorName
══════════════════════════════════════════════════════════ */